import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { userInventoryAtom } from 'src/stores/auth/atoms';
import {
  type ItemDetail,
  INVENTORY_ITEM_CATEGORY_ID_TYPES,
  ItemType,
} from 'src/types/itemInventory';
import { sortDateValidUntil } from 'src/utils/itemInventory';

export const isExpire = (time: number) => new Date(time).getTime() < Date.now();

/**
 * 웹에서 제공하지 않는 기능과 관련 아이템은 인벤토리에서 제공하지 않도록 처리
 * @see https://hpcnt.slack.com/archives/C02SQS5AJDR/p1771580496545759?thread_ts=1771565745.668989&cid=C02SQS5AJDR
 */
export const HIDDEN_CATEGORY_IDS = new Set<string>([
  INVENTORY_ITEM_CATEGORY_ID_TYPES.VIRTUAL_LOCATION,
  INVENTORY_ITEM_CATEGORY_ID_TYPES.INACT_GENDER_PERIOD,
  INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_PICK_FILTER_COUNT,
  INVENTORY_ITEM_CATEGORY_ID_TYPES.PICK_MATCH_DISCOUNT,
]);

type ItemLists = {
  consumableItems: ItemDetail[];
  periodicItems: ItemDetail[];
  subscriptionItems: ItemDetail[];
};

const useInventoryItemList = (): ItemLists => {
  const itemInventoryList = useAtomValue(userInventoryAtom);
  return useMemo(() => {
    const itemMaps: Record<ItemType, Map<number, ItemDetail>> = {
      [ItemType.SUBSCRIPTION]: new Map(),
      [ItemType.CONSUMABLE]: new Map(),
      [ItemType.PERIODIC]: new Map(),
    };

    itemInventoryList?.forEach(
      ({ detailJson: { groupedItems = [], guide } = {}, categoryId, itemSource }) => {
        if (HIDDEN_CATEGORY_IDS.has(categoryId)) return;
        // 구독 소스(WEB/GOOGLE_PLAY/APP_STORE)에서 온 아이템 중 Core, Max만 인벤토리에 표시하고, 나머지 구독 혜택 아이템은 숨긴다.
        if (
          (itemSource === 'WEB_SUBSCRIPTION' ||
            itemSource === 'GOOGLE_PLAY_SUBSCRIPTION' ||
            itemSource === 'APP_STORE_SUBSCRIPTION') &&
          categoryId !== INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE &&
          categoryId !== INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX
        ) {
          return;
        }

        const displayNameMap = new Map();

        // useCount가 없거나 dateValidUntil이 존재하는 경우엔 별개의 아이템으로 노출
        groupedItems
          .filter(
            ({ displayItem, dateValidUntil }) =>
              displayItem && (dateValidUntil == null || !isExpire(dateValidUntil))
          )
          .forEach((item) => {
            const { displayName, inventoryId } = item;
            const mapKey = `${displayName}${
              !item.useCount || item.dateValidUntil ? inventoryId : ''
            }`;
            const { useCount: prevCount = 0 } = displayNameMap.get(mapKey) || {};

            displayNameMap.set(mapKey, {
              ...item,
              guide,
              useCount: item.useCount + prevCount,
              categoryId,
            });
          });

        displayNameMap.forEach((item: ItemDetail) => {
          if (item.itemType) {
            itemMaps[item.itemType].set(item.inventoryId, item);
          }
        });
      }
    );

    return {
      consumableItems: Array.from(itemMaps[ItemType.CONSUMABLE].values()).sort(sortDateValidUntil),
      periodicItems: Array.from(itemMaps[ItemType.PERIODIC].values()).sort(sortDateValidUntil),
      subscriptionItems: Array.from(itemMaps[ItemType.SUBSCRIPTION].values()),
    };
  }, [itemInventoryList]);
};

export default useInventoryItemList;
